<template>
  <v-container>
    <v-card>
      <v-card-title>
        Events Catalog
        <v-spacer />
        <v-btn color="primary" @click="openDialog()">Add New</v-btn>
      </v-card-title>

      <!-- Filters -->
      <v-card-text>
        <v-expansion-panels focusable inset flat>
          <v-expansion-panel>
            <v-expansion-panel-header>Filters</v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row dense>
                <v-col cols="12" sm="6" md="3">
                  <catValueDropDown
                    :outlined="true"
                    :catName="'EVENT_TYPES'"
                    v-model="filters.eventType"
                    label="Event Type"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-select
                    v-model="filters.status"
                    :items="['planned', 'active', 'archived', 'cancelled']"
                    label="Status"
                    dense
                    clearable
                  />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-menu
                    v-model="filters.startMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="filters.startDate"
                        label="From Date"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <v-date-picker
                      v-model="filters.startDate"
                      @change="filters.startMenu = false"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-menu
                    v-model="filters.endMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="filters.endDate"
                        label="To Date"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <v-date-picker
                      v-model="filters.endDate"
                      @change="filters.endMenu = false"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-checkbox
                    v-model="filters.privateOnly"
                    label="Private only"
                    dense
                  />
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-data-table
        :headers="headers"
        :items="filteredCatalogs"
        item-key="eventId"
        class="elevation-1"
        show-expand
        :expanded.sync="expanded"
        :items-per-page="itemsPerPage"
        :sort-by="'startDate'"
        :sort-desc="false"
      >
        <template v-slot:item.actions="{ item }">
          <v-icon small class="mr-2" @click="openDialog(item)">edit</v-icon>
          <v-icon small @click="confirmDelete(item.eventId)">delete</v-icon>
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-5">
            <v-expansion-panels>
              <v-expansion-panel>
                <WorkoutSchedule :term-id="Number(item.eventId)" />
              </v-expansion-panel>
            </v-expansion-panels>
          </td>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add/Edit Event Dialog -->
    <v-dialog v-model="dialog" max-width="800px">
      <v-card>
        <v-card-title>{{ form.eventId ? "Edit" : "Add" }} Event</v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.name" label="Name" />
              </v-col>
              <v-col cols="12" sm="6">
                <catValueDropDown
                  :outlined="true"
                  :catName="'EVENT_TYPES'"
                  v-model="form.eventType"
                  label="Event Type"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="form.visibility"
                  label="Visibility"
                  :items="['private', 'public']"
                  dense
                  clearable
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="form.status"
                  :items="['planned', 'active', 'archived', 'cancelled']"
                  label="Status"
                  dense
                  clearable
                />
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="form.description" label="Description" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu
                  ref="startDateMenu"
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.startDate"
                      label="Start Date"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="form.startDate"
                    @change="startDateMenu = false"
                  />
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu
                  ref="endDateMenu"
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.endDate"
                      label="End Date"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="form.endDate"
                    @change="endDateMenu = false"
                  />
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.facilityId" label="Facility ID" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.urlToDetails"
                  label="URL to Details"
                />
              </v-col>
              <!-- <v-col cols="12" sm="6">
                <v-text-field v-model="form.eventId" label="Create Date" />
              </v-col> -->

              <v-col cols="12" sm="6">
                <v-text-field v-model="form.createDate" label="Create Date" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="form.updateDate" label="Update Date" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.updateSource"
                  label="Update Source"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="blue darken-1" @click="dialog = false"
            >Cancel</v-btn
          >
          <v-btn text color="green darken-1" @click="saveEvent">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import WorkoutSchedule from "./WorkoutSchedule.vue";
import catValueDropDown from "../../shared/cat_val_dropdown.vue";

export default {
  components: {
    WorkoutSchedule,
    catValueDropDown,
  },
  data() {
    return {
      dialog: false,
      form: {},
      itemsPerPage: 50,
      expanded: [],
      startDateMenu: false,
      endDateMenu: false,
      filters: {
        eventType: null,
        status: null,
        startDate: null,
        endDate: null,
        startMenu: false,
        endMenu: false,
        privateOnly: false,
      },
      headers: [
        { text: "Event ID", value: "eventId" },
        { text: "Org Type", value: "orgType" },
        { text: "Visibility", value: "visibility" },
        { text: "Org ID", value: "orgId" },
        { text: "Name", value: "name" },
        { text: "Event Type", value: "eventType" },
        { text: "Status", value: "status" },
        { text: "Start Date", value: "startDate" },
        { text: "End Date", value: "endDate" },
        { text: "Description", value: "description" },
        { text: "Facility ID", value: "facilityId" },
        { text: "URL to Details", value: "urlToDetails" },
        { text: "Create Date", value: "createDate" },
        { text: "Update Date", value: "updateDate" },
        { text: "Update Source", value: "updateSource" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters(["eventCatalogs", "user", "club"]),
    filteredCatalogs() {
      return this.eventCatalogs.filter((item) => {
        const matchesEventType =
          !this.filters.eventType || item.eventType === this.filters.eventType;
        const matchesStatus =
          !this.filters.status || item.status === this.filters.status;
        const matchesPrivate =
          !this.filters.privateOnly || item.visibility === "private";
        const matchesStart =
          !this.filters.startDate ||
          new Date(item.startDate) >= new Date(this.filters.startDate);
        const matchesEnd =
          !this.filters.endDate ||
          new Date(item.endDate) <= new Date(this.filters.endDate);
        return (
          matchesEventType &&
          matchesStatus &&
          matchesPrivate &&
          matchesStart &&
          matchesEnd
        );
      });
    },
  },
  created() {
    this.loadEvents();
  },
  methods: {
    ...mapActions([
      "allEventCatalog",
      "eventCatalogMerge",
      "eventCatalogDelete",
    ]),

    async loadEvents() {
      try {
        await this.allEventCatalog();
      } catch (error) {
        console.error("Failed to load Events:", error);
      }
    },

  openDialog(item = {}) {
      this.form = {
        ...item,
        // Ensure required org fields default to current club context
        orgType: "club",
        orgId: Number(this.club),
        clubId: this.club
      };
      this.dialog = true;
  },

    saveEvent() {
      this.form.updateSource = this.user.userName;
      // Enforce required org fields for backend (String! + Int!)
      this.form.orgType = "club";
      this.form.orgId = Number(this.club);

      const invalidKeys = ["__typename", "clubId", "createDate", "updateDate"];

      const input = Object.fromEntries(
        Object.entries(this.form).filter(([key]) => !invalidKeys.includes(key))
      );

      this.eventCatalogMerge({ programData: input }).then(() => {
        this.dialog = false;
      });
  },

    confirmDelete(eventId) {
      console.log(eventId);
      if (confirm("Are you sure you want to delete this Event?")) {
        this.eventCatalogDelete({ eventId: parseInt(eventId) });
      }
    }
  }
};
</script>
